import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { Button } from "@/design-system/Button";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import type {
  CommunityFeedAnalyticsDateRange,
  CommunityFeedAnalyticsGroup,
} from "@/queries/analytics/community-feed-reporting-types";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { CommunityFeedAnalyticsContent } from "./CommunityFeedAnalyticsContent";
import {
  CommunityFeedAnalyticsFilterDrawer,
  type CommunityFeedAnalyticsFilterValues,
} from "./CommunityFeedAnalyticsFilterDrawer";

export interface CommunityFeedAnalyticsDateRangeOption {
  value: CommunityFeedAnalyticsDateRange;
  name: string;
}

export function CommunityFeedAnalyticsPage({ mode = "focused" }: { mode?: "focused" | "all" }): React.ReactNode {
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const analyticsScope = useAnalyticsScope();
  const [selectedDateRange, setSelectedDateRange] = useState<CommunityFeedAnalyticsDateRange>("last6Months");
  const [filter, setFilter] = useState<CommunityFeedAnalyticsFilterValues>({
    authorType: "all",
    contentType: "all",
    groupId: "all",
    origin: "all",
    status: "all",
  });
  const [isFilterOpened, filterOpenHandlers] = useBool();

  const dateRangeOptions = useMemo<NonEmptyArray<CommunityFeedAnalyticsDateRangeOption>>(
    () => [
      { value: "last6Months", name: t("common.date-range.last-months", { count: 6 }) },
      { value: "lastYear", name: t("common.date-range.last-year") },
    ],
    [t],
  );
  const selectedDateRangeOption = dateRangeOptions.find((option) => option.value === selectedDateRange)!;
  const query = {
    dateRange: selectedDateRange,
    authorType: filter.authorType === "all" ? undefined : filter.authorType,
    contentType: filter.contentType === "all" ? undefined : filter.contentType,
    groupId: filter.groupId === "all" ? undefined : filter.groupId,
    origin: filter.origin === "all" ? undefined : filter.origin,
    status: filter.status === "all" ? undefined : filter.status,
  };
  const projectResult = useQuery({
    ...analyticsQueries.communityFeedReporting(query),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "project",
  });
  const portfolioResult = useQuery({
    ...analyticsQueries.portfolioCommunityFeedReporting({
      ...query,
      projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [],
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "portfolio" && !analyticsScope.isLoading,
  });
  const { data, isLoading, isFetching, error } = analyticsScope.type === "portfolio" ? portfolioResult : projectResult;
  const showOriginFilter = (data?.originCounts.projectConnection ?? 0) > 0;
  const handleGroupFilter = useCallback((groupId: string) => {
    setFilter((current) => ({ ...current, groupId }));
  }, []);
  const groupColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<CommunityFeedAnalyticsGroup>();

    return [
      helper.accessor("name", {
        header: t("page.analytics-community-feed.groups.audience"),
        cell: (cell) => (
          <Button variant="subtle" size="sm" onClick={() => handleGroupFilter(cell.row.original.id)}>
            {cell.getValue()}
          </Button>
        ),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("type", {
        header: t("page.analytics-community-feed.groups.type"),
        cell: (cell) => t(`page.analytics-community-feed.group-type.${cell.getValue()}` as const),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("posts", {
        header: t("page.analytics-community-feed.groups.posts"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("residentContributors", {
        header: t("page.analytics-community-feed.groups.contributors"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("comments", {
        header: t("page.analytics-community-feed.groups.comments"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("interactionsPerPost", {
        header: t("page.analytics-community-feed.groups.interactions-per-post"),
        cell: (cell) => cell.getValue().toFixed(1),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
    ];
  }, [handleGroupFilter, t]);
  const groupTable = useResizableTable(TABLE_KEYS.ANALYTICS_COMMUNITY_FEED_GROUPS, {
    columns: groupColumns,
    data: data?.groups ?? [],
    initialState: {
      sorting: [{ id: "posts", desc: true }],
    },
  });
  const activeFilterCount =
    Number(filter.authorType !== "all") +
    Number(filter.contentType !== "all") +
    Number(filter.groupId !== "all") +
    Number(showOriginFilter && filter.origin !== "all") +
    Number(filter.status !== "all");
  const handleDateRangeChange = (option: CommunityFeedAnalyticsDateRangeOption) => {
    setSelectedDateRange(option.value);
  };
  const handleFilterUpdate = (value: CommunityFeedAnalyticsFilterValues) => {
    setFilter(value);
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <CommunityFeedAnalyticsContent
        mode={mode}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        selectedDateRange={selectedDateRangeOption}
        dateRangeOptions={dateRangeOptions}
        onDateRangeChange={handleDateRangeChange}
        activeFilterCount={activeFilterCount}
        onOpenFilters={filterOpenHandlers.setTrue}
        groupTable={groupTable}
        contentType={filter.contentType}
      />
      <CommunityFeedAnalyticsFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandlers.set}
        filter={filter}
        groups={data?.filterOptions.groups ?? []}
        showOriginFilter={showOriginFilter}
        onUpdate={handleFilterUpdate}
      />
    </>
  );
}
